// Qué se refresca al volver (segundo plano, pantalla apagada, red, hub
// reconectado, API reiniciada). Puro: recibe el resultado del reenvío y el
// contexto, y devuelve la lista de piezas de estado a pedir. Lo ejecuta
// `tpvResync.runResumeRefresh`.
//
// Contrato:
//  - "replay"  → el reenvío (`GET realtime/sync`) ya repuso TODOS los eventos
//                perdidos por los mismos handlers que en directo. Solo falta
//                lo que no viaja por la secuencia (presencia, lease, add-ons,
//                datos del restaurante, KDS) y solo si estuvo fuera un rato.
//  - "snapshot"→ el servidor no tiene el hueco (más de 60 min, demasiados
//                eventos, reinicio del API / epoch nuevo) o no hay secuencia
//                (hubs legacy): estado completo en paralelo.
//  - "failed"  → no se pudo ni preguntar: igual que snapshot (si tampoco hay
//                red, fallará y el chip de "Reconectando…" lo indicará).

/** Fuera más de esto → además de reponer eventos, refresca lo que no viaja por la secuencia. */
export const OUT_OF_BAND_AFTER_MS = 60_000;

export const KEYS = {
  mesas: "mesas",
  checkout: "checkout",
  comensales: "comensales",
  presence: "presence",
  lease: "lease",
  lastPayment: "lastPayment",
  onlyOrders: "onlyOrders",
  bookings: "bookings",
  notifications: "notifications",
  addOns: "addOns",
  user: "user",
  cashDrawer: "cashDrawer",
  catalog: "catalog",
  kds: "kds",
};

const validId = (id) => id != null && id !== -1 && id !== "-1" && id !== "";

/**
 * @param {object} p
 * @param {"none"|"replay"|"snapshot"|"failed"} p.mode
 * @param {number} [p.awayMs]
 * @param {boolean} [p.catalog]       fuerza catálogo (epoch nuevo / products.changed perdido)
 * @param {string}  [p.path]          ruta actual
 * @param {object}  [p.state]         estado redux
 * @param {string}  [p.tpvPath]       prefijo de la ruta TPV (lista de pedidos en uso)
 */
export function planResumeRefresh({ mode, awayMs = 0, catalog = false, path = "", state = {}, tpvPath = "" } = {}) {
  const keys = new Set([KEYS.presence, KEYS.lease]);
  const selectedId = state?.mesas?.selectedId;
  const hasSelected = validId(selectedId);
  const onKds = /\/displays\/[^/]+/.test(path || "") && validId(state?.kitchenTickets?.currentWorkingDisplay);
  const pendingCash = Object.keys(state?.cashdrawer?.pendingTx || {}).length > 0;
  const ordersInUse =
    (Array.isArray(state?.order?.onlyOrders) && state.order.onlyOrders.length > 0) ||
    (!!tpvPath && (path || "").startsWith(tpvPath));

  const full = mode === "snapshot" || mode === "failed";
  const longAway = awayMs >= OUT_OF_BAND_AFTER_MS;

  if (full) {
    keys.add(KEYS.mesas);
    keys.add(KEYS.lastPayment);
    keys.add(KEYS.bookings);
    keys.add(KEYS.notifications);
    if (hasSelected) {
      keys.add(KEYS.checkout);
      keys.add(KEYS.comensales);
    }
    if (ordersInUse) keys.add(KEYS.onlyOrders);
    if (pendingCash) keys.add(KEYS.cashDrawer);
    // Sin el hueco no sabemos si se perdió un `products.changed`.
    if (mode === "snapshot") keys.add(KEYS.catalog);
  }
  if (catalog) keys.add(KEYS.catalog);
  if (full || longAway) {
    keys.add(KEYS.addOns);
    keys.add(KEYS.user);
  }
  // El KDS va por su hub propio, sin secuencia: cualquier hueco puede haber
  // perdido tickets → siempre se re-pide en la pantalla de cocina.
  if (onKds) keys.add(KEYS.kds);
  return Array.from(keys);
}
